import React, { useCallback, useContext, useEffect, useRef, useState } from "react"
import "./style.css";
import { VehicleMap } from "../../Components/Molecules"
import { VehiclesList } from "../../Components/Organisms";
import Loader from "../Loader";
import { LIVE_TRACKING_HEADERS, LIVE_TRACKING_HEADERS_FOR_B2B } from "../../Library/tableHeaders";
import { StoreContext } from "../../Library/contexts";
import { AutoCompleteMultiSelect } from "../../Components/Atoms";
import { getAlerts, getAllDealers, getStatesByCountryId } from "../../APIs";
import { AddModelIcon, LocationIcon } from "../../Assets/images/svgs";
import { DRAWER_ACCESS_CODES, GEOFENCE_TRIGGER_TYPE, SELLER_DES, SUBDEALERS_DES } from "../../Library/constants";
import { Button, useToast } from "@chakra-ui/react";
import { getItemFromLocalStorage, transformUserDetails, getDaysLeft } from "../../Library/helpers";
import { useNavigate } from "react-router-dom";
import PATHS from "../../Routes/paths";

const LiveTracking = () => {
  const [ showLoading, setShowLoading ] = useState(true);
  const [ vehicles, setVehicles ] = useState([]);
  const [ vids, setVids ] = useState([]);
  const [ vehiclesMap, setVehiclesMap ] = useState(new Map());
  const [ vehicleTableStats, setVehicleTableStats ] = useState(new Map());
  const [ highlightedVehicle, setHighlightedVehicle ] = useState({});
  const [ fetchedStates, setFetchedStates ] = useState([]);
  const [ showGeofences, setShowGeofences ] = useState(false);
  const [ geofencesToShow, setGeofencesToShow ] = useState([]);
  const [ isStateFilterActive, setIsStateFilterActive ] = useState(false);
  const [ statesWithUserVehs, setStatesWithUserVehs ] = useState([]);
  const [ allDealers, setAllDealers ] = useState([]);

  const originalVehicleData = useRef([]);
  const vehiclesCountCalculated = useRef(false);
  const Toast = useToast();
  const navigate = useNavigate();
  const geofencesFetched = useRef(false);
  const DESIGNATION = getItemFromLocalStorage("des");
  const LiveTrackingPageRef = useRef(null);
  const dealersFetched = useRef(false);

  const { userVehicles, allStates, allGeofences, setAllGeofences, ensureSubsWarrantyData } = useContext(StoreContext);

  const [expiringVehicles, setExpiringVehicles] = useState([]);

  useEffect(() => {
    const ACCESS_CODES = getItemFromLocalStorage("accessCodes");
    if (!ACCESS_CODES.includes(DRAWER_ACCESS_CODES.PLANS_AND_WARRANTY)) return;
    ensureSubsWarrantyData({}, (err, data) => {
      if (err) return;
      const vehs = transformUserDetails(data);
      const expiring = vehs.filter(v => {
        const days = getDaysLeft(v.subscriptionExpiry);
        return days <= 15 && days > -90;
      });
      setExpiringVehicles(expiring);
    });
  }, [ensureSubsWarrantyData]);

  const handleFetchedVehicles = (vehicles, dontResetVids) => {
    setVehicles(vehicles);
    if(!dontResetVids && vids.length != vehicles.length) setVids(vehicles.map((veh) => veh.idx));
    let vehMap = new Map();
    vehicles.forEach(vehicle => {
      vehMap.set(vehicle.idx, vehicle);
    });

    setVehiclesMap(vehMap);
    setShowLoading(false);
  }

  const handleVehicleHighlight = useCallback((vehicle) => {
    if (vehicle.zoom) return;

    const updatedVehicles = vehicles.map(veh => ({
      ...veh,
      highlight: veh.idx === vehicle.vid,
    }));

    updatedVehicles.sort((a, b) => {
      if (a.highlight && !b.highlight) return -1;
      if (!a.highlight && b.highlight) return 1;
      return 0;
    });

    handleFetchedVehicles(updatedVehicles, true);
    setHighlightedVehicle(updatedVehicles.find(veh => veh.highlight));
  }, [vehicles]);


  useEffect(() => {
    handleFetchedVehicles(userVehicles);
  }, [ userVehicles ]);

  const handleScrollTop = () => {
    LiveTrackingPageRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  }

  useEffect(() => {
    global.EE.addEventListener(global.EE.EVENT_TYPES.VEHICLE_INFO_SHOWN, handleVehicleHighlight);
    global.EE.addEventListener(global.EE.EVENT_TYPES.MAP_ICON_CLICKED, handleScrollTop);

    return () => {
      global.EE.removeEventListener(global.EE.EVENT_TYPES.VEHICLE_INFO_SHOWN, handleVehicleHighlight);
      global.EE.removeEventListener(global.EE.EVENT_TYPES.MAP_ICON_CLICKED, handleScrollTop);
    }
  }, [handleVehicleHighlight]);

  const fetchAllStates = async () => {
    if (allStates && allStates.length) {
      setFetchedStates(allStates);
      return;
    }

    let states = await getStatesByCountryId(true);
    if (states && states.length) {
      states.forEach(state => {
        state.label = state.nm;
        state.value = state.idx;
      });

      setFetchedStates(states);
    }
  }

  useEffect(() => {
    fetchAllStates();
  }, []);

  useEffect(() => {
    if (!vehicles || !vehicles.length || !fetchedStates || !fetchedStates.length || vehiclesCountCalculated.current) return;
    let statesToShow = [];
    fetchedStates.forEach(state => {
      let stateHasVehicles = vehicles.filter(data => data.reg && data.reg.toLowerCase().startsWith(state.abb?.toLowerCase()));
      if (stateHasVehicles && stateHasVehicles.length) {
        state.label = `${state.nm} (${stateHasVehicles.length})`;
        statesToShow.push(state);
      }
    });

    setStatesWithUserVehs(statesToShow);
    vehiclesCountCalculated.current = true;
    setFetchedStates(statesToShow);

  }, [ vehicles, fetchedStates ]);

  const handleFilterApply = (selectedOptions, isSelectAll) => {
    if (!selectedOptions.length) return;

    if (!originalVehicleData.current || !originalVehicleData.current.length) originalVehicleData.current = vehicles;

    let filteredVehicles = [];
    if (isSelectAll) filteredVehicles = originalVehicleData.current;
    else {
      selectedOptions.forEach(option => {
        let filteredVehs = originalVehicleData.current.filter(data => {
          if (data && option && data.reg && option.abb) return data.reg.toLowerCase().startsWith(option.abb?.toLowerCase());
          return "";
        });
        filteredVehicles = filteredVehicles.concat(filteredVehs);
      });
    }

    if (!isStateFilterActive) setIsStateFilterActive(true);
    setVehicles(filteredVehicles);
    setVids(filteredVehicles.map(vehicle => vehicle.idx));
    let vehMap = new Map();
    filteredVehicles.forEach(vehicle => {
      vehMap.set(vehicle.idx, vehicle);
    });
    setVehiclesMap(vehMap);
  }

  const handleViewGeofences = (isChecked) => {
    if ((allGeofences && allGeofences.length) || geofencesFetched.current) setGeofencesToShow(allGeofences);
    else {
      getAlerts(GEOFENCE_TRIGGER_TYPE, (err, data) => {
        if(err) console.error("Error fetching geofence alerts", err);
        else {
          geofencesFetched.current = true;

          setAllGeofences(data);
          setGeofencesToShow(data);
          if (!data.length) Toast({ isClosable: true, description: "You haven't added any geofences yet", position: "bottom", variant: "subtle", duration: 3000 })
        }
      });
    }

    setShowGeofences(isChecked);
  }

  useEffect(() => {
    if (DESIGNATION === SELLER_DES || DESIGNATION === SUBDEALERS_DES) {
      if (!dealersFetched.current && (!allDealers || !allDealers.length)) {
        dealersFetched.current = true;

        getAllDealers(null, (err, dealers) => {
          if (err) console.log(err)
          else setAllDealers(dealers);
        });
      }
    }
  }, [ allDealers ]);

  const handleVehicleTableData = (vehicleStats) => {
    const userVehicleMap = new Map(userVehicles.map(v => [v.idx, v]));
    const newVehStats = new Map();

    vehicleStats.forEach((stat, vid) => {
      const veh = userVehicleMap.get(vid);
      newVehStats.set(vid, { ...stat, veh: veh || stat.veh });
    });

    setVehicleTableStats(newVehStats);
  }

  const getSortedVehicles = () => {
    return vehicles.sort((veh1, veh2) => {
      const ts1 = veh1.ping?.ts;
      const ts2 = veh2.ping?.ts;
      if (!ts1) return 1;
      if (!ts2) return -1;
      return ts2 - ts1;
    });
  }

  return (
    <>
      {!showLoading ? <section ref={LiveTrackingPageRef} className="panel-main live-tracking">
        <header className="live-tracking-header">
          <div className="select-state-dropdown">
            {statesWithUserVehs && statesWithUserVehs.length > 1 ?  <AutoCompleteMultiSelect autoFillOptions={fetchedStates} options={fetchedStates} placeholder={"All States"} icon={<LocationIcon />} handleFilterApply={handleFilterApply} showListControls={true} /> : null}
          </div>
          <Button leftIcon={<AddModelIcon />} className="add-btn" onClick={() => navigate(`${PATHS.LIVE_TRACKING}/${PATHS.MAP_DEVICE}?lt=true`)}>
            Add New Vehicle
          </Button>
        </header>
        <div className="lt-map">
          {expiringVehicles.length > 0 && (
            <div className="map-chip-container">
              <button className="expiring-map-chip" onClick={() => navigate(PATHS.PLANS_AND_WARRANTY)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                {expiringVehicles.length} Vehicle{expiringVehicles.length > 1 ? 's' : ''} subscription expiring soon
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
              </button>
            </div>
          )}
          {(vids && vids.length) ? <VehicleMap isSellerOrDealer={(DESIGNATION === SELLER_DES || DESIGNATION === SUBDEALERS_DES)} isStateFilterActive={isStateFilterActive} showExistingGeofences={showGeofences} setShowExistingGeofences={handleViewGeofences} hideGeofenceMarkers={true} vids={vids} vehMap={vehiclesMap} allGeofences={geofencesToShow} updateVehiclesTable={handleVehicleTableData} showExistingGfsToggle={true} /> : <p className="no-map-message">There are no vehicles available</p>}
        </div>

        <VehiclesList showVehOnMapIcon={true} isSellerOrDealer={(DESIGNATION === SELLER_DES || DESIGNATION === SUBDEALERS_DES)} dealers={allDealers} tableHeaders={(DESIGNATION === SELLER_DES || DESIGNATION === SUBDEALERS_DES) ? LIVE_TRACKING_HEADERS_FOR_B2B : LIVE_TRACKING_HEADERS} allVehicles={getSortedVehicles()} vehicleStats={vehicleTableStats} highlightedVehicle={highlightedVehicle} />
      </section> : <Loader hide={true} />}
    </>
  )
}

export default LiveTracking
